import { useEffect, useRef } from 'react';
import INFINITE_SCROLL_CONFIG from '../../../config/infiniteScrollConfig';

export const useInfiniteScroll = (hasMore, loadingMore, loading, isSearching, onLoadMore) => {
  const observerRef = useRef();
  const observerInstanceRef = useRef();

  useEffect(() => {
    console.log('🔄 Observer recreating with states:', {
      hasMore,
      loadingMore,
      loading,
      isSearching
    });

    // Limpiar observer anterior si existe
    if (observerInstanceRef.current) {
      observerInstanceRef.current.disconnect();
    }

    // Crear nuevo observer
    observerInstanceRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        
        // Verificar que el elemento está visible y que se puede cargar más
        console.log('�️ Scroll detected:', {
          visible: entry.isIntersecting,
          hasMore,
          loading: loadingMore || loading || isSearching
        });

        if (
          entry.isIntersecting && 
          hasMore && 
          !loadingMore && 
          !loading && 
          !isSearching
        ) {
          console.log('🔄 Triggering load more...');
          onLoadMore();
        }
      },
      {
        ...INFINITE_SCROLL_CONFIG.OBSERVER_CONFIG,
        // Añadir más margen para mejor UX
        rootMargin: '200px'
      }
    );

    // Función para conectar el observer
    const connectObserver = () => {
      if (observerRef.current && observerInstanceRef.current) {
        observerInstanceRef.current.observe(observerRef.current);
        console.log('👁️ Observer connected to element');
        return true;
      }
      return false;
    };

    // Intentar conectar inmediatamente
    if (!connectObserver()) {
      console.log('⚠️ Element not ready, will retry...');
      // Si no se puede conectar, intentar después de un breve delay
      const retryTimeout = setTimeout(() => {
        if (!connectObserver()) {
          console.log('⚠️ Element still not available after retry');
        }
      }, 100);

      // Cleanup del timeout
      return () => {
        clearTimeout(retryTimeout);
        if (observerInstanceRef.current) {
          observerInstanceRef.current.disconnect();
        }
      };
    }

    // Cleanup
    return () => {
      if (observerInstanceRef.current) {
        observerInstanceRef.current.disconnect();
      }
    };
  }, [hasMore, loadingMore, loading, isSearching, onLoadMore]);

  // Efecto adicional para reconectar el observer cuando el elemento esté disponible
  useEffect(() => {
    if (observerRef.current && observerInstanceRef.current) {
      // Desconectar primero si ya estaba conectado
      observerInstanceRef.current.disconnect();
      // Reconectar al nuevo elemento
      observerInstanceRef.current.observe(observerRef.current);
      console.log('🔗 Observer reconnected to new element');
    }
  }, [observerRef.current]); // Ejecutar cuando observerRef.current cambie

  // Cleanup adicional cuando el componente se desmonta
  useEffect(() => {
    return () => {
      if (observerInstanceRef.current) {
        observerInstanceRef.current.disconnect();
      }
    };
  }, []);

  return observerRef;
};
